import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

function scrollToAnchor(targetId, smooth = true) {
  const target = document.getElementById(targetId);
  if (!target) return;

  target.scrollIntoView({
    behavior: smooth ? 'smooth' : 'auto',
    block: 'start'
  });
}

function scrollToAnchorFromHash() {
  const hash = window.location.hash;
  if (!hash) return;

  const targetId = hash.replace('#', '');
  const target = document.getElementById(targetId);
  if (!target) return;

  setTimeout(() => {
    scrollToAnchor(targetId, true);
  }, 350);
}

document.addEventListener('click', function (e) {
  const btn = e.target.closest('[data-scroll-to-anchor]');
  if (!btn) return;

  const targetId = btn.getAttribute('data-scroll-to-anchor');
  const cartDrawer = btn.closest('cart-drawer-component');

  const isHomePage =
    window.location.pathname === '/' ||
    document.body.classList.contains('is-home');

  if (!isHomePage) {
    return;
  }

  e.preventDefault();

  if (cartDrawer) {
    if (typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    } else if (typeof cartDrawer.closeDialog === 'function') {
      cartDrawer.closeDialog();
    }
  }

  setTimeout(() => {
    document.body.classList.remove('overflow-hidden', 'no-scroll', 'scroll-locked');
    document.documentElement.classList.remove('overflow-hidden', 'no-scroll', 'scroll-locked');

    const dialog = cartDrawer?.querySelector('dialog');
    if (dialog) {
      dialog.removeAttribute('inert');
    }

    scrollToAnchor(targetId, true);
    history.replaceState(null, '', '#' + targetId);
  }, 300);
});

document.addEventListener('DOMContentLoaded', scrollToAnchorFromHash);
window.addEventListener('load', scrollToAnchorFromHash);